import { shallowFilter, toggleFilter } from "@schema-benchmarks/utils";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import * as v from "valibot";

import { DownloadCount } from "#src/routes/_benchmarks/-components/count";
import { BenchResults } from "#src/routes/_benchmarks/_runtime/-components/results";
import {
  dataTypeProps,
  optimizeTypeProps,
  optionalDataTypeSchema,
  optionalOptimizeTypeSchema,
  sortParamsEntries,
} from "#src/routes/_benchmarks/_runtime/-constants";
import { useSortedResults } from "#src/routes/_benchmarks/_runtime/-hooks";
import { getBenchResults } from "#src/routes/_benchmarks/_runtime/-query";
import { PageFilters } from "#src/shared/components/page-filter";
import { PageFilterChips } from "#src/shared/components/page-filter/chips";
import { generateMetadata } from "#src/shared/data/meta";
import { getHighlightedCode } from "#src/shared/lib/highlight";

import Content from "./content.mdx";

const searchSchema = v.object({
  optimizeType: optionalOptimizeTypeSchema,
  dataType: optionalDataTypeSchema,
  ...sortParamsEntries,
});

export const Route = createFileRoute("/_benchmarks/_runtime/validation/")({
  validateSearch: searchSchema,
  component: RouteComponent,
  loaderDeps: ({ search: { optimizeType, dataType } }) => ({
    optimizeType,
    dataType,
  }),
  async loader({ context: { queryClient }, deps: { optimizeType, dataType }, abortController }) {
    const benchResults = await queryClient.ensureQueryData(getBenchResults(abortController.signal));
    await Promise.all(
      Object.values(
        benchResults.validation[dataType].filter(shallowFilter({ optimizeType })),
      ).flatMap(({ snippet, libraryName }) => [
        DownloadCount.prefetch(libraryName, {
          queryClient,
          signal: abortController.signal,
        }),
        queryClient.prefetchQuery(getHighlightedCode({ code: snippet }, abortController.signal)),
      ]),
    );
  },
  head: () =>
    generateMetadata({
      title: "Validation",
      description: "Benchmark results for validating data.",
      openGraph: {
        url: "/validation/",
      },
    }),
  staticData: { crumb: "Validation" },
});

function RouteComponent() {
  const { optimizeType, dataType, sortBy, sortDir } = Route.useSearch();
  const { data } = useSuspenseQuery({
    ...getBenchResults(),
    select: (results) => results.validation[dataType].filter(shallowFilter({ optimizeType })),
  });
  const sortedData = useSortedResults(data, sortBy, sortDir);
  return (
    <>
      <Content components={{ wrapper: "div" }} />
      <PageFilters>
        <PageFilterChips
          {...dataTypeProps}
          getLinkOptions={(option) => ({
            from: Route.fullPath,
            to: "/validation",
            search: toggleFilter("dataType", option),
            replace: true,
            resetScroll: false,
          })}
        />
        <PageFilterChips
          {...optimizeTypeProps}
          getLinkOptions={(option) => ({
            from: Route.fullPath,
            to: "/validation",
            replace: true,
            search: toggleFilter("optimizeType", option),
            resetScroll: false,
          })}
        />
      </PageFilters>
      <BenchResults results={sortedData} to="/validation" {...{ sortBy, sortDir }} />
    </>
  );
}
